/* ==========================================================================
   WAYPOINT — tiny hand-rolled history router. No react-router, no hash
   routing. pushState/replaceState + popstate, exposed via
   useSyncExternalStore so route reads are consistent with React rendering.
   ========================================================================== */

import { useSyncExternalStore, type MouseEvent, type ReactNode } from 'react';

let currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
const listeners = new Set<() => void>();

function emit() {
  currentPath = window.location.pathname;
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', emit);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string {
  return currentPath;
}

export function useLocation(): string {
  return useSyncExternalStore(subscribe, getSnapshot, () => '/');
}

export function navigate(path: string, opts?: { replace?: boolean }): void {
  if (path === currentPath) return;
  if (opts?.replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
  emit();
}

export function goBack(): void {
  window.history.back();
}

/** Matches a route pattern like "/trips/:id" against a concrete path.
 * Returns the param map on match, or null. */
export function matchRoute(
  pattern: string,
  path: string,
): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    const p = patternParts[i];
    const v = pathParts[i];
    if (p.startsWith(':')) {
      params[p.slice(1)] = decodeURIComponent(v);
    } else if (p !== v) {
      return null;
    }
  }
  return params;
}

export interface LinkProps {
  to: string;
  replace?: boolean;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  'aria-label'?: string;
}

export function Link({ to, replace, className, children, onClick, ...rest }: LinkProps) {
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let browser handle new-tab etc.
    e.preventDefault();
    onClick?.();
    navigate(to, { replace });
  };
  return (
    <a href={to} className={className} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
